import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { API, formatApiErrorDetail } from "@/context/AuthContext";
import { useVisitorProfile } from "@/hooks/useVisitorProfile";
import GoogleAddressInput from "@/components/GoogleAddressInput";

const INITIAL = { name: "", phone: "", email: "", address: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const { hasConsent, setConsent, save, hydrate, clear } = useVisitorProfile();
  const [remember, setRemember] = useState(hasConsent);

  // Pre-fill from the visitor's saved profile on first mount so returning
  // customers never retype name/phone/email/address.
  useEffect(() => {
    hydrate(setForm);
  }, [hydrate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.phone.trim()) return toast.error("Please enter your phone number.");
    if (!form.email.trim()) return toast.error("Please enter your email.");
    if (!form.address.trim()) return toast.error("Please enter your address.");
    if (!form.message.trim()) return toast.error("Please tell us what needs cleaning.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        message: form.message.trim(),
        service: "Pressure washing",
      });
      // Persist (or clear) the visitor's contact details for next time.
      if (remember) {
        setConsent(true);
        save(form);
      } else {
        setConsent(false);
      }
      toast.success("Thanks! We'll give you a call back shortly.");
      // Preserve the saved profile fields on reset so the form stays pre-filled.
      setForm((prev) => ({
        ...INITIAL,
        ...(remember
          ? { name: prev.name, phone: prev.phone, email: prev.email, address: prev.address }
          : {}),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const forgetMe = () => {
    clear();
    setConsent(false);
    setRemember(false);
    setForm(INITIAL);
    toast.success("Your saved info was cleared from this device.");
  };

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-stone-100 self-start"
    >
      <h3 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: "Outfit" }}>
        Get my free quote
      </h3>
      <p className="text-sm text-stone-500 mb-8">Drop a few details and we'll call you back — any hour, any day.</p>

      <div className="space-y-5">
        <Field label="Your name">
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            data-testid="quote-name-input"
            className={INPUT}
            placeholder="Sarah Thompson"
            autoComplete="name"
            required
          />
        </Field>
        <Field label="Your phone number">
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            data-testid="quote-phone-input"
            className={`${INPUT} text-lg`}
            placeholder="(555) 555-1234"
            autoComplete="tel"
            required
          />
        </Field>
        <Field label="Your email">
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            data-testid="quote-email-input"
            className={INPUT}
            placeholder="you@email.com"
            autoComplete="email"
            required
          />
        </Field>
        <Field label="Service address">
          <GoogleAddressInput
            value={form.address}
            onChange={(v) => setForm((prev) => ({ ...prev, address: v }))}
            testId="quote-address-input"
            className={INPUT}
            placeholder="Start typing — pick your address from the suggestions"
            required
          />
        </Field>
        <Field label="What needs cleaning?">
          <textarea
            value={form.message}
            onChange={update("message")}
            data-testid="quote-message-input"
            rows={4}
            className={`${INPUT} resize-none`}
            placeholder="e.g. Driveway and front sidewalk — about 800 sq ft. Two-story house wash."
            required
          />
        </Field>
        <button
          type="submit"
          disabled={submitting}
          data-testid="quote-submit-btn"
          className="w-full px-6 py-4 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-full transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
        >
          {submitting ? "Sending..." : "Request a call back"}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>

        {/* Remember-me opt-in: visitor-controlled, stored on their device only. */}
        <label
          data-testid="quote-remember-toggle"
          className="flex items-start gap-3 cursor-pointer group select-none"
        >
          <span
            className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
              remember
                ? "bg-brand-600 border-brand-600"
                : "border-stone-300 group-hover:border-brand-400 bg-white"
            }`}
            aria-hidden="true"
          >
            {remember && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
          </span>
          <input
            type="checkbox"
            className="sr-only"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            data-testid="quote-remember-checkbox"
          />
          <span className="text-xs text-stone-700 leading-relaxed">
            <span className="font-semibold">Remember my info on this device</span>
            <span className="text-stone-500"> — so you don&apos;t have to type it again next time. (Stored only in your browser.)</span>
          </span>
        </label>

        {hasConsent && (
          <button
            type="button"
            onClick={forgetMe}
            data-testid="quote-forget-btn"
            className="text-xs text-stone-500 hover:text-stone-700 underline underline-offset-2 self-center"
          >
            Clear my saved info
          </button>
        )}

        <p className="text-xs text-center text-stone-500">
          By submitting, you&apos;re saying yes to a friendly call back. That&apos;s all.
        </p>
      </div>
    </form>
  );
}

const INPUT =
  "w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white focus:border-transparent transition-all";

function Field({ label, children }) {
  const required = !label.toLowerCase().includes("optional");
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.18em] text-stone-600 mb-2 block">
        {label}
        {required && <span className="text-brand-700 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
